import React, { useState } from 'react';
import { X, Zap, Globe, Phone, Mail, MessageSquare, Users, Database, GitBranch, Clock, Search, Star, TrendingUp, Shield } from 'lucide-react';

const TEMPLATES = [
  {
    id: 'lead-capture-crm',
    name: 'Lead Capture → CRM Sync',
    description: 'Automatically sync new leads to GoHighLevel CRM with scoring and tagging.',
    category: 'Lead Generation',
    icon: Users,
    color: 'blue',
    popular: true,
    nodes: [
      { id: 'webhook_trigger', label: 'New Lead Webhook', type: 'trigger', icon: Globe },
      { id: 'condition', label: 'Score > 70?', type: 'condition', icon: GitBranch },
      { id: 'ghl_create_contact', label: 'Create GHL Contact', type: 'action', icon: Globe },
      { id: 'email_send', label: 'Send Welcome Email', type: 'action', icon: Mail },
    ]
  },
  {
    id: 'high-score-dialer',
    name: 'High-Score Lead → Dialer Queue',
    description: 'Add hot leads (score ≥ 85) to the AI Dialer queue automatically.',
    category: 'Sales Automation',
    icon: Phone,
    color: 'green',
    popular: true,
    nodes: [
      { id: 'db_trigger', label: 'Lead Score Updated', type: 'trigger', icon: Database },
      { id: 'condition', label: 'Score ≥ 85?', type: 'condition', icon: GitBranch },
      { id: 'dialer_add_queue', label: 'Add to Dialer Queue', type: 'action', icon: Phone },
      { id: 'sms_send', label: 'SMS Notification', type: 'action', icon: MessageSquare },
    ]
  },
  {
    id: 'deal-won-followup',
    name: 'Deal Won → Follow-up Sequence',
    description: 'Trigger a follow-up email and SMS sequence when a deal is marked as won.',
    category: 'Customer Success',
    icon: Star,
    color: 'purple',
    popular: false,
    nodes: [
      { id: 'webhook_trigger', label: 'Deal Won Event', type: 'trigger', icon: Globe },
      { id: 'db_trigger', label: 'Update DB Record', type: 'action', icon: Database },
      { id: 'email_send', label: 'Send Thank You Email', type: 'action', icon: Mail },
      { id: 'schedule_trigger', label: 'Schedule Follow-up', type: 'action', icon: Clock },
    ]
  },
  {
    id: 'email-drip-campaign',
    name: 'Email Drip Campaign',
    description: 'Automated multi-step email nurture sequence for new subscribers.',
    category: 'Email Marketing',
    icon: Mail,
    color: 'orange',
    popular: true,
    nodes: [
      { id: 'webhook_trigger', label: 'New Subscriber', type: 'trigger', icon: Zap },
      { id: 'email_send', label: 'Day 1: Welcome', type: 'action', icon: Mail },
      { id: 'delay', label: 'Wait 3 Days', type: 'condition', icon: Clock },
      { id: 'email_send', label: 'Day 4: Value Email', type: 'action', icon: Mail },
      { id: 'delay', label: 'Wait 4 Days', type: 'condition', icon: Clock },
      { id: 'email_send', label: 'Day 8: Offer Email', type: 'action', icon: Mail },
    ]
  },
  {
    id: 'sms-reengagement',
    name: 'SMS Re-engagement Campaign',
    description: 'Re-engage cold leads with a targeted SMS sequence.',
    category: 'Re-engagement',
    icon: MessageSquare,
    color: 'red',
    popular: false,
    nodes: [
      { id: 'schedule_trigger', label: 'Scheduled Trigger', type: 'trigger', icon: Clock },
      { id: 'condition', label: 'Lead Inactive 30d?', type: 'condition', icon: GitBranch },
      { id: 'sms_send', label: 'Send Re-engage SMS', type: 'action', icon: MessageSquare },
      { id: 'delay', label: 'Wait 2 Days', type: 'condition', icon: Clock },
      { id: 'sms_sequence', label: 'Follow-up SMS', type: 'action', icon: MessageSquare },
    ]
  },
  {
    id: 'pipeline-stage-update',
    name: 'Pipeline Stage Auto-Update',
    description: 'Automatically move contacts through GHL pipeline stages based on actions.',
    category: 'CRM Automation',
    icon: TrendingUp,
    color: 'teal',
    popular: false,
    nodes: [
      { id: 'webhook_trigger', label: 'Email Opened', type: 'trigger', icon: Mail },
      { id: 'ghl_update_status', label: 'Update GHL Status', type: 'action', icon: Globe },
      { id: 'ghl_move_pipeline', label: 'Move Pipeline Stage', type: 'action', icon: GitBranch },
      { id: 'sms_send', label: 'Notify Sales Rep', type: 'action', icon: MessageSquare },
    ]
  },
  {
    id: 'lead-scoring-alert',
    name: 'Lead Score Alert System',
    description: 'Alert your team when a lead reaches a high score threshold.',
    category: 'Lead Scoring',
    icon: Shield,
    color: 'yellow',
    popular: false,
    nodes: [
      { id: 'db_trigger', label: 'Score Change Event', type: 'trigger', icon: Database },
      { id: 'condition', label: 'Score ≥ 90?', type: 'condition', icon: GitBranch },
      { id: 'email_send', label: 'Alert Sales Team', type: 'action', icon: Mail },
      { id: 'dialer_add_queue', label: 'Priority Dial Queue', type: 'action', icon: Phone },
    ]
  },
  {
    id: 'appointment-reminder',
    name: 'Appointment Reminder Sequence',
    description: 'Send automated reminders before scheduled appointments.',
    category: 'Scheduling',
    icon: Clock,
    color: 'indigo',
    popular: false,
    nodes: [
      { id: 'schedule_trigger', label: '24h Before Appt', type: 'trigger', icon: Clock },
      { id: 'email_send', label: 'Email Reminder', type: 'action', icon: Mail },
      { id: 'delay', label: 'Wait 20 Hours', type: 'condition', icon: Clock },
      { id: 'sms_send', label: '4h SMS Reminder', type: 'action', icon: MessageSquare },
    ]
  },
];

const CATEGORIES = ['All', 'Lead Generation', 'Sales Automation', 'Email Marketing', 'CRM Automation', 'Re-engagement', 'Customer Success', 'Lead Scoring', 'Scheduling'];

const colorMap = {
  blue: 'bg-blue-100 text-blue-600',
  green: 'bg-green-100 text-green-600',
  purple: 'bg-purple-100 text-purple-600',
  orange: 'bg-orange-100 text-orange-600',
  red: 'bg-red-100 text-red-600',
  teal: 'bg-teal-100 text-teal-600',
  yellow: 'bg-yellow-100 text-yellow-600',
  indigo: 'bg-indigo-100 text-indigo-600',
};

const nodeTypeColor = {
  trigger: 'bg-purple-100 text-purple-700',
  action: 'bg-blue-100 text-blue-700',
  condition: 'bg-yellow-100 text-yellow-700',
  output: 'bg-green-100 text-green-700',
};

const TemplateLibrary = ({ onUseTemplate, onClose }) => {
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  const [previewTemplate, setPreviewTemplate] = useState(null);

  const filtered = TEMPLATES?.filter(t => {
    const matchesSearch = !search ||
      t?.name?.toLowerCase()?.includes(search?.toLowerCase()) ||
      t?.description?.toLowerCase()?.includes(search?.toLowerCase()) ||
      t?.category?.toLowerCase()?.includes(search?.toLowerCase());
    const matchesCategory = activeCategory === 'All' || t?.category === activeCategory;
    return matchesSearch && matchesCategory;
  });

  const popular = filtered?.filter(t => t?.popular);
  const rest = filtered?.filter(t => !t?.popular);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Workflow Templates</h2>
            <p className="text-sm text-gray-500 mt-0.5">Choose a pre-built template to get started quickly</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Search & Filters */}
        <div className="px-6 py-3 border-b border-gray-100 space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e?.target?.value)}
              placeholder="Search templates..."
              className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
            {CATEGORIES?.map(cat => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                  activeCategory === cat
                    ? 'bg-blue-600 text-white' :'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* Template Grid */}
        <div className="flex-1 overflow-y-auto p-6">
          {previewTemplate ? (
            // Preview Mode
            (<div>
              <button
                onClick={() => setPreviewTemplate(null)}
                className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 mb-4 transition-colors"
              >
                ← Back to templates
              </button>
              <div className="bg-gray-50 rounded-xl p-6">
                <div className="flex items-start gap-4 mb-6">
                  <div className={`p-3 rounded-xl ${colorMap?.[previewTemplate?.color]}`}>
                    <previewTemplate.icon className="w-6 h-6" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="text-lg font-bold text-gray-900">{previewTemplate?.name}</h3>
                      {previewTemplate?.popular && (
                        <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-xs font-medium rounded-full">Popular</span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mt-1">{previewTemplate?.description}</p>
                    <span className="inline-block mt-2 px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded-full">{previewTemplate?.category}</span>
                  </div>
                </div>
                <h4 className="text-sm font-semibold text-gray-700 mb-3">Workflow Steps ({previewTemplate?.nodes?.length} blocks)</h4>
                <div className="flex flex-col gap-2">
                  {previewTemplate?.nodes?.map((node, i) => (
                    <div key={i} className="flex items-center gap-3">
                      {i > 0 && <div className="w-0.5 h-4 bg-gray-300 ml-4 -mt-2 mb-0" />}
                      <div className="flex items-center gap-3 bg-white border border-gray-200 rounded-lg px-4 py-2.5 w-full">
                        <span className="w-6 h-6 rounded-full bg-gray-100 text-gray-600 text-xs font-bold flex items-center justify-center flex-shrink-0">{i + 1}</span>
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${nodeTypeColor?.[node?.type] || 'bg-gray-100 text-gray-600'}`}>{node?.type}</span>
                        <span className="text-sm font-medium text-gray-800">{node?.label}</span>
                      </div>
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => onUseTemplate?.(previewTemplate)}
                  className="mt-6 w-full py-3 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 transition-colors"
                >
                  Use This Template
                </button>
              </div>
            </div>)
          ) : (
            // Grid Mode
            (<div>
              {popular?.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">⭐ Popular Templates</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {popular?.map(template => (
                      <TemplateCard
                        key={template?.id}
                        template={template}
                        onPreview={() => setPreviewTemplate(template)}
                        onUse={() => onUseTemplate?.(template)}
                        colorMap={colorMap}
                      />
                    ))}
                  </div>
                </div>
              )}
              {rest?.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">All Templates</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {rest?.map(template => (
                      <TemplateCard
                        key={template?.id}
                        template={template}
                        onPreview={() => setPreviewTemplate(template)}
                        onUse={() => onUseTemplate?.(template)}
                        colorMap={colorMap}
                      />
                    ))}
                  </div>
                </div>
              )}
              {filtered?.length === 0 && (
                <div className="text-center py-12">
                  <Search className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500 font-medium">No templates found</p>
                  <p className="text-sm text-gray-400 mt-1">Try a different search or category</p>
                </div>
              )}
            </div>)
          )}
        </div>
      </div>
    </div>
  );
};

const TemplateCard = ({ template, onPreview, onUse, colorMap }) => (
  <div className="bg-white border border-gray-200 rounded-xl p-4 hover:border-blue-300 hover:shadow-md transition-all group">
    <div className="flex items-start gap-3 mb-3">
      <div className={`p-2 rounded-lg ${colorMap?.[template?.color]}`}>
        <template.icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <h4 className="text-sm font-semibold text-gray-900 truncate">{template?.name}</h4>
          {template?.popular && (
            <span className="flex-shrink-0 w-4 h-4 text-orange-400">⭐</span>
          )}
        </div>
        <span className="text-xs text-blue-600 font-medium">{template?.category}</span>
      </div>
    </div>
    <p className="text-xs text-gray-500 mb-3 line-clamp-2">{template?.description}</p>
    <div className="flex items-center gap-1 mb-3">
      {template?.nodes?.slice(0, 3)?.map((node, i) => (
        <span key={i} className="text-xs px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded">{node?.label?.split(' ')?.[0]}</span>
      ))}
      {template?.nodes?.length > 3 && (
        <span className="text-xs text-gray-400">+{template?.nodes?.length - 3} more</span>
      )}
    </div>
    <div className="flex gap-2">
      <button
        onClick={onPreview}
        className="flex-1 py-1.5 text-xs font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
      >
        Preview
      </button>
      <button
        onClick={onUse}
        className="flex-1 py-1.5 text-xs font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
      >
        Use Template
      </button>
    </div>
  </div>
);

export default TemplateLibrary;
